import { StarsButton, RawToggle as Toggle, Translation } from '@ps/web-shared-components';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import type { CashGame } from '@swf/generated/graphql/poker/CG/schemas';
import { Tooltip } from '@swf/poker';

import InfoIcon from '../../../assets/info.svg';
import ZoomIcon from '../../../assets/zoom.svg';
import useCGHandleRegister from '../../../hooks/useCGHandleRegister';
import useSimpleGamesSubscription from '../../../hooks/useSimpleGamesSubscription';
import { usePokerCashGamesWidget } from '../../../model';
import type { SliderValues } from '../../../types/';
import { TableType } from '../../../types/';
import StakeSelector from '../StakeSelector/StakeSelector';

import {
  TXTCLI_Format,
  TXTCLI_Play_Now,
  TXTCLI_Regular,
  TXTCLI_RegularX_Regular_is_the_classi_ELL,
  TXTCLI_ZoomX_Zoom_is_a_fast_paced_for_ELL
} from './GameSelector.messages.json';
import styles from './GameSelector.scss';

const GameSelector: React.FC = () => {
  const { widgetDispatch, widgetSelect } = usePokerCashGamesWidget();
  const activeTableType = useSelector(widgetSelect.getActiveSimpleTableType);

  const { data, loading, error } = useSimpleGamesSubscription();

  const queryData = useMemo(() => {
    if (!data?.cashGamesGrouped) return null;
    return data.cashGamesGrouped as CashGame[];
  }, [data]);

  const activeCashGames = useMemo(() => {
    if (!queryData) return [];

    const filtered = queryData.reduce((acc: SliderValues[], game) => {
      if (game && game.tableType === activeTableType) {
        const {
          bettingLimit,
          blindStructure,
          minBuyIn,
          maxBuyIn,
          gameType,
          tableType,
          tableSize,
          currency
        } = game;
        const { smallBlind, bigBlind } = blindStructure || {};

        acc.push({
          smallBlind,
          bigBlind,
          minBuyIn,
          maxBuyIn,
          gameType,
          tableType,
          tableSize,
          currency,
          bettingLimit
        });
      }
      return acc;
    }, []);

    return filtered.length > 0 ? filtered : [];
  }, [queryData, activeTableType]);

  const handleRegisterOpen = useCGHandleRegister(activeCashGames);

  return (
    <div className={styles.container}>
      <div className={styles.description}>
        <Translation {...TXTCLI_Format} />
      </div>
      <div className={styles.formatGroup}>
        <div className={styles.toggleGroup}>
          <Toggle
            id="format-regular"
            name="gameFormat"
            label={<Translation {...TXTCLI_Regular} />}
            onOffToggle
            type="radio"
            key={TableType.REGULAR}
            value={TableType.REGULAR}
            defaultChecked={activeTableType === TableType.REGULAR}
            onChange={() => {
              widgetDispatch.setActiveSimpleTableType(TableType.REGULAR);
            }}
          />
          <Toggle
            id="format-zoom"
            name="gameFormat"
            label={<ZoomIcon />}
            onOffToggle
            type="radio"
            key={TableType.ZOOM}
            value={TableType.ZOOM}
            defaultChecked={activeTableType === TableType.ZOOM}
            onChange={() => {
              widgetDispatch.setActiveSimpleTableType(TableType.ZOOM);
            }}
          />
        </div>
        <Tooltip
          content={
            <>
              <Translation tagName="p" {...TXTCLI_RegularX_Regular_is_the_classi_ELL} />
              <Translation tagName="p" {...TXTCLI_ZoomX_Zoom_is_a_fast_paced_for_ELL} />
            </>
          }
          arrowClassName={styles.tooltipArrow}
          tooltipClassName={styles.tooltip}
          placement="top-end"
          refElementClassName={styles.tooltipWrapper}
        >
          <InfoIcon />
        </Tooltip>
      </div>
      <StakeSelector cashGames={activeCashGames} isLoading={loading} queryError={error?.message} />
      <StarsButton
        className={styles.submitButton}
        type="submit"
        block
        variant="primary"
        disabled={loading || !!error?.message}
        translation={TXTCLI_Play_Now}
        onClick={handleRegisterOpen}
      />
    </div>
  );
};

export default GameSelector;
